"use client";

import { TGetListingProductsResponseDTO } from "@/app/api/listing/[slug]/products/listing-products.dto";
import { cn } from "@/lib/utils";
import { SearchIcon, Loader2Icon } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { TAGS_LISTING_API } from "@/lib/constants";
import { TTagListing } from "@/types/tags";
import { useQuery } from "@tanstack/react-query";

type ProductsTabSearchAndFilterProps = {
	initialResults: TGetListingProductsResponseDTO;
	setResults: React.Dispatch<React.SetStateAction<TGetListingProductsResponseDTO>>;
	isLoading: boolean;
	disableSearch?: boolean;
};

async function getTagListingData() {
	const res = await fetch(TAGS_LISTING_API, { next: { revalidate: 10 } });

	if (!res.ok) {
		const { message } = await res.json();
		throw new Error(message);
	}

	const { data } = (await res.json()) as { data: TTagListing[] };
	return data;
}

const ProductsTabSearchAndFilter = ({
	initialResults,
	setResults,
	isLoading,
	disableSearch,
}: ProductsTabSearchAndFilterProps) => {
	const searchRef = useRef<HTMLInputElement | null>(null);
	const [selectedTag, setSelectedTag] = useState<string>("all");

	const {
		isLoading: isTagsDataLoading,
		isError,
		data: tagsList = [],
	} = useQuery({ queryKey: ["tagsList"], queryFn: getTagListingData });

	const tags = tagsList.map((tag) => tag.name);

	const handleTagFilter = useCallback(
		(tagName: string) => {
			if (tagName === "all") {
				setSelectedTag(tagName);
				setResults(initialResults);
				return;
			}

			setSelectedTag(tagName);

			const filteredResults = initialResults.filter((product) =>
				product.tags?.some((tag) => tag?.name === tagName)
			);

			setResults(filteredResults.length ? filteredResults : initialResults);
		},
		[initialResults, setResults]
	);

	const handleSearch = useCallback(
		(query: string) => {
			if (query.trim() === "") {
				setResults(initialResults);
				return;
			}

			const filteredResults = initialResults.filter((product) =>
				product.name?.toLowerCase().includes(query.toLowerCase())
			);

			setResults(filteredResults);
		},
		[initialResults, setResults]
	);

	if (isLoading || isTagsDataLoading) {
		return <Loader2Icon className="mx-auto h-6 w-6 animate-spin stroke-blue-500" />;
	}

	if (isError) {
		return <div className="text-red-500">Failed to load tags.</div>;
	}

	return (
		<div className="flex w-full flex-col items-center justify-center gap-8 rounded-lg bg-white px-8 py-4 text-sm shadow-sm lg:flex-row lg:items-center">
			<div
				className={cn(
					"flex items-center justify-between gap-4 px-2",
					disableSearch ? "max-w-sm sm:max-w-md lg:max-w-2xl" : "max-w-md"
				)}
			>
				<div className="flex items-center gap-4 overflow-x-auto" style={{ scrollbarWidth: "thin" }}>
					<div
						className="flex items-center justify-start gap-4 overflow-x-scroll pb-2"
						style={{
							scrollbarWidth: "thin",
							scrollbarColor: "brand-primary transparent",
						}}
						id="categoriesList"
					>
						<button
							className={cn(
								"flex items-center gap-1 rounded-lg p-2",
								selectedTag === "all"
									? "bg-brand-primary text-white"
									: "bg-transparent text-gray-600"
							)}
							onClick={() => handleTagFilter("all")}
						>
							All
						</button>

						{tags?.map((tagName) => (
							<button
								key={tagName}
								className={cn(
									"flex items-center gap-1 rounded-lg bg-transparent p-2 text-gray-400",
									selectedTag === tagName
										? "bg-brand-primary text-white"
										: "bg-transparent text-gray-600"
								)}
								onClick={() => handleTagFilter(tagName)}
							>
								<span className="whitespace-nowrap break-keep text-sm">{tagName}</span>
							</button>
						))}
					</div>
				</div>
			</div>

			{!disableSearch && (
				<div className="flex w-[30%]">
					<div
						className={cn(
							"group flex w-full items-center rounded border border-gray-200 bg-white px-4 py-2",
							"transition-colors duration-150 ease-linear focus-within:border-brand-primary"
						)}
					>
						<span>
							<SearchIcon className="h-4 w-4 text-gray-400 transition-colors duration-150 ease-linear group-focus-within:text-blue-500" />
						</span>

						<input
							ref={searchRef}
							id="search"
							type="search"
							name="search"
							placeholder="Search"
							className="w-full px-2 text-sm focus:outline-none"
							onChange={(e) => handleSearch(e.target.value)}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export default ProductsTabSearchAndFilter;
